import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import i18next from '../../setup/locales/i18n';

import type { AppDispatch } from '../store';
import type { PlannerItemStatusesEnum } from '@shared/enums/plannerItemStatuses.enum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { CreateEventFromPayloadModel } from '@shared/models/event/createEventFormPayload.model';
import type { EventItemModel } from '@shared/models/event/eventItem.model';
import type { ItemUserModel } from '@shared/models/itemUser.model';

export const createEventItem = createAsyncThunk<
  EventItemModel,
  CreateEventFromPayloadModel,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('createEventItem', async (params, { rejectWithValue }) => {
  try {
    return await api.event.createEvent(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateUsersEvent = createAsyncThunk<
  EventItemModel,
  { users: ItemUserModel[]; eventId: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateUsersEvent', async (params, { rejectWithValue }) => {
  try {
    return await api.event.updateUsersEvent(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateEventItem = createAsyncThunk<
  EventItemModel,
  { params: CreateEventFromPayloadModel; eventId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('updateEventItem', async ({ params, eventId, confirmation_status }, { rejectWithValue }) => {
  try {
    return await api.event.updateEvent(params, eventId, confirmation_status);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deleteEvent = createAsyncThunk<
  number,
  { eventId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('deleteEvent', async (params, { rejectWithValue }) => {
  try {
    return await api.event.deleteEvent(params).then(() => params.eventId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const removeMyselfFromEvent = createAsyncThunk<
  number,
  { eventId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('removeMyselfFromEvent', async (params, { rejectWithValue }) => {
  try {
    return await api.event.removeMyself(params).then(() => params.eventId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const archiveEvent = createAsyncThunk<
  number,
  { eventId: number; confirmation_status?: string; is_full: boolean },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('archiveEvent', async (params, { rejectWithValue }) => {
  try {
    return await api.event.archiveEvent(params).then(() => params.eventId);
  } catch (e: any) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const unArchiveEvent = createAsyncThunk<
  number,
  { eventId: number; confirmation_status?: string; is_full: boolean },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('unArchiveEvent', async (params, { rejectWithValue }) => {
  try {
    return await api.event.unArchiveEvent(params).then(() => params.eventId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const changeEventStatus = createAsyncThunk<
  EventItemModel,
  { eventId: number; status: PlannerItemStatusesEnum; is_common?: boolean; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('changeEventStatus', async (params, { rejectWithValue }) => {
  try {
    return await api.event.changeStatus(params);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getEvent = createAsyncThunk<
  EventItemModel,
  { eventId: number; is_list?: number },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getEvent', async (props, { rejectWithValue }) => {
  try {
    return await api.event.getEvent(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(
      e,
      {
        [StatusServerErrors.AccessDenied]: {
          message: i18next.t('general.notifications.notAssignedToEvent'),
        },
        [StatusServerErrors.ItemNotFound]: {
          message: i18next.t('general.notifications.eventNotFound'),
        },
      },
      {
        notificationVariant: 'info',
      },
    );

    return rejectWithValue(axiosError.data);
  }
});
